import axios, { AxiosInstance } from 'axios';
import {
  AccessMethod,
  ActiveSession,
  ActivityFilters,
  ActivityLog,
  ActivityResponse,
  AuthResponse,
  ContractorPolicies,
  CreateDeviceFormData,
  CreateUserFormData,
  DashboardStats,
  Device,
  DeviceCredential,
  DeviceGrant,
  DevicesResponse,
  LoginFormData,
  Recording,
  RecordingFilters,
  RecordingsResponse,
  SessionsResponse,
  UpdateDeviceFormData,
  User,
  UsersResponse,
} from '../types/api';

class ApiService {
  public api: AxiosInstance;

  constructor() {
    console.log('ApiService constructor');
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
    console.log('API Base URL:', apiBaseUrl);
    this.api = axios.create({
      baseURL: apiBaseUrl,
      withCredentials: true,
      timeout: 30000,
    });

    // Request interceptor for debugging
    this.api.interceptors.request.use((config) => {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
      return config;
    });

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API Error:', error.response?.data || error.message);
        if (error.response?.status === 401) {
          // Handle unauthorized - redirect to login if needed
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication
  async login(credentials: LoginFormData): Promise<AuthResponse> {
    const response = await this.api.post<AuthResponse>('/api/auth/login', credentials);
    return response.data;
  }

  async logout(): Promise<void> {
    await this.api.post('/api/auth/logout');
  }

  async checkAuth(): Promise<AuthResponse> {
    const response = await this.api.get<AuthResponse>('/api/auth/check');
    return response.data;
  }

  // Users
  async getUsers(): Promise<User[]> {
    const response = await this.api.get<UsersResponse>('/api/admin/users');
    return response.data.users;
  }

  async createUser(userData: CreateUserFormData): Promise<User> {
    const response = await this.api.post<User>('/api/admin/users', userData);
    return response.data;
  }

  async updateUser(userId: number, userData: Partial<CreateUserFormData>): Promise<User> {
    const response = await this.api.put<User>(`/api/admin/users/${userId}`, userData);
    return response.data;
  }

  async deleteUser(userId: number): Promise<void> {
    await this.api.delete(`/api/admin/users/${userId}`);
  }

  async bulkDeleteUsers(userIds: number[]): Promise<void> {
    await this.api.post('/api/admin/users/bulk-delete', { userIds });
  }

  // Devices
  async getDeviceCredentials(deviceId: number): Promise<DeviceCredential[]> {
    const response = await this.api.get(`/api/admin/devices/${deviceId}/credentials`);
    return response.data.credentials;
  }

  async createDeviceCredential(
    deviceId: number,
    data: { method: AccessMethod; label: string; username: string; password?: string; domain?: string }
  ): Promise<DeviceCredential> {
    const response = await this.api.post(`/api/admin/devices/${deviceId}/credentials`, data);
    return response.data.credential;
  }

  async updateDeviceCredential(
    credentialId: number,
    data: { label?: string; username?: string; password?: string; domain?: string }
  ): Promise<DeviceCredential> {
    const response = await this.api.put(`/api/admin/credentials/${credentialId}`, data);
    return response.data.credential;
  }

  async deleteDeviceCredential(credentialId: number): Promise<void> {
    await this.api.delete(`/api/admin/credentials/${credentialId}`);
  }

  async getGrants(deviceId?: number): Promise<DeviceGrant[]> {
    const response = await this.api.get('/api/admin/grants', {
      params: deviceId ? { deviceId } : undefined,
    });
    return response.data.grants;
  }

  async upsertGrant(data: {
    userId: number;
    deviceId: number;
    credentialId?: number | null;
    allowedMethods?: AccessMethod[];
    expiresAt?: string | null;
  }): Promise<void> {
    await this.api.post('/api/admin/grants', data);
  }

  async revokeGrant(userId: number, deviceId: number): Promise<void> {
    await this.api.delete(`/api/admin/grants/${userId}/${deviceId}`);
  }

  async getDevices(): Promise<Device[]> {
    const response = await this.api.get<DevicesResponse>('/api/devices');
    return response.data.devices;
  }

  async createDevice(deviceData: CreateDeviceFormData): Promise<Device> {
    const response = await this.api.post<Device>('/api/admin/devices', deviceData);
    return response.data;
  }

  async updateDevice(deviceId: number, updates: Partial<UpdateDeviceFormData>): Promise<Device> {
    const response = await this.api.put<Device>(`/api/admin/devices/${deviceId}`, updates);
    return response.data;
  }

  async deleteDevice(deviceId: number): Promise<void> {
    await this.api.delete(`/api/admin/devices/${deviceId}`);
  }

  async setDefaultDevice(deviceId: number): Promise<void> {
    await this.api.post(`/api/admin/devices/${deviceId}/default`);
  }

  async pauseDevice(deviceId: number): Promise<void> {
    await this.api.post(`/api/admin/devices/${deviceId}/pause`);
  }

  async resumeDevice(deviceId: number): Promise<void> {
    await this.api.post(`/api/admin/devices/${deviceId}/resume`);
  }

  // Recordings
  async getRecordings(filters?: RecordingFilters): Promise<Recording[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await this.api.get<RecordingsResponse>(`/api/recordings?${params}`);
    return response.data.recordings;
  }

  async getRecording(recordingId: number): Promise<Recording> {
    const response = await this.api.get<Recording>(`/api/recordings/${recordingId}`);
    return response.data;
  }

  async deleteRecording(recordingId: number): Promise<void> {
    await this.api.delete(`/api/recordings/${recordingId}`);
  }

  async bulkDeleteRecordings(recordingIds: number[]): Promise<void> {
    await this.api.post('/api/recordings/bulk-delete', { recordingIds });
  }

  async getRecordingEvents(recordingId: number): Promise<any[]> {
    const response = await this.api.get(`/api/recordings/${recordingId}/events`);
    return response.data.events;
  }

  async downloadRecording(recordingId: number): Promise<Blob> {
    const response = await this.api.get(`/api/recordings/${recordingId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Sessions
  async getActiveSessions(): Promise<ActiveSession[]> {
    const response = await this.api.get<SessionsResponse>('/api/admin/sessions');
    return response.data.sessions;
  }

  async terminateSession(sessionId: string): Promise<void> {
    await this.api.post(`/api/admin/sessions/${sessionId}/terminate`);
  }

  // Activity Log
  async getActivityLog(filters?: ActivityFilters): Promise<ActivityLog[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, value.toString());
        }
      });
    }

    const response = await this.api.get<ActivityResponse>(`/api/admin/activity?${params}`);
    return response.data.activities;
  }

  // Policies
  async getPolicies(): Promise<ContractorPolicies> {
    const response = await this.api.get<ContractorPolicies>('/api/admin/policies');
    return response.data;
  }

  async updatePolicies(policies: Partial<ContractorPolicies>): Promise<ContractorPolicies> {
    const response = await this.api.put<ContractorPolicies>('/api/admin/policies', policies);
    return response.data;
  }

  // Dashboard Stats
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.api.get<DashboardStats>('/api/admin/stats');
    return response.data;
  }


  // File operations
  downloadFile(url: string, filename: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export const apiService = new ApiService();
export default apiService;